import React, { useState, useEffect } from 'react';
import { AlertCircle, HelpCircle } from 'lucide-react';

interface DialogState {
  isOpen: boolean;
  type: 'alert' | 'confirm';
  message: string;
}

let setDialogStateGlobal: ((state: DialogState | null) => void) | null = null;
let dialogResolver: ((value: boolean) => void) | null = null;

// Overriding global window.alert
window.alert = (message: string): any => {
  return new Promise<void>((resolve) => {
    dialogResolver = () => resolve();
    if (setDialogStateGlobal) {
      setDialogStateGlobal({ isOpen: true, type: 'alert', message });
    } else {
      console.warn('CustomDialogContainer not mounted. Falling back to console.');
      console.log('Alert:', message);
      resolve();
    }
  });
};

// Overriding global window.confirm
window.confirm = (message: string): any => {
  return new Promise<boolean>((resolve) => {
    dialogResolver = resolve;
    if (setDialogStateGlobal) {
      setDialogStateGlobal({ isOpen: true, type: 'confirm', message });
    } else {
      console.warn('CustomDialogContainer not mounted. Falling back to default confirm.');
      resolve(false);
    }
  });
};

export function CustomDialogContainer() {
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  useEffect(() => {
    setDialogStateGlobal = setDialogState;
    return () => {
      setDialogStateGlobal = null;
    };
  }, []);

  if (!dialogState || !dialogState.isOpen) return null;

  const handleConfirm = () => {
    setDialogState(null);
    if (dialogResolver) {
      dialogResolver(true);
      dialogResolver = null;
    }
  };

  const handleCancel = () => {
    setDialogState(null);
    if (dialogResolver) {
      dialogResolver(false);
      dialogResolver = null;
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md transition-all duration-300">
      <div className="w-full max-w-[440px] mx-4 bg-[#0a0a0a]/95 border border-white/10 rounded-3xl p-7 shadow-2xl flex flex-col gap-5 transform scale-100 transition-transform duration-300">
        <div className="flex items-start gap-4">
          <div className={`p-3.5 rounded-2xl flex items-center justify-center ${dialogState.type === 'confirm' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>
            {dialogState.type === 'confirm' ? (
              <HelpCircle className="w-6 h-6" />
            ) : (
              <AlertCircle className="w-6 h-6" />
            )}
          </div>
          <div className="flex-1 space-y-1.5">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-white/30">
              {dialogState.type === 'confirm' ? 'Confirm Action' : 'Notification'}
            </h3>
            <p className="text-white/90 text-sm leading-relaxed whitespace-pre-line font-medium">
              {dialogState.message}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3.5 mt-2">
          {dialogState.type === 'confirm' && (
            <button
              onClick={handleCancel}
              className="px-6 py-3 rounded-2xl text-xs font-bold text-white/60 bg-white/5 border border-white/10 hover:bg-white/10 transition-all cursor-pointer"
            >
              취소
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`px-6 py-3 rounded-2xl text-xs font-bold text-black shadow-lg transition-all cursor-pointer ${dialogState.type === 'confirm' ? 'bg-blue-500 hover:bg-blue-400' : 'bg-white hover:bg-white/95'}`}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
